import { useState, useEffect } from 'react';
import { getCallbackRequests, updateCallbackRequest } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { Phone, Clock, CheckCircle, User, Mail, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const CallbacksPage = () => {
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await getCallbackRequests();
      setRequests(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateCallbackRequest(id, newStatus);
      toast.success('Statut mis à jour');
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#F9C80E]/10 text-[#F9C80E]">
            <Clock className="w-3 h-3" /> En attente
          </span>
        );
      case 'contacted':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#397DFF]/10 text-[#397DFF]">
            <Phone className="w-3 h-3" /> Contacté
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#10D183]/10 text-[#10D183]">
            <CheckCircle className="w-3 h-3" /> Terminé
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="callbacks-loading">
        <Skeleton className="h-10 w-48 bg-white" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-32 bg-white" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="callbacks-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#191D1E]">
          Demandes de rappel
        </h1>
        <p className="text-[#687175] mt-1">
          {requests.filter(r => r.status === 'pending').length} demande(s) en attente
        </p>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-12 text-center hover:shadow-md transition-shadow duration-200">
          <Phone className="w-12 h-12 text-[#BFC6CC] mx-auto mb-4" />
          <p className="text-[#687175]">Aucune demande de rappel</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request, index) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 hover:shadow-md transition-shadow duration-200"
              data-testid={`callback-card-${request.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F5F6F9] flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-[#687175]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#191D1E]">{request.client_name}</h3>
                    <div className="flex items-center gap-2 text-sm text-[#687175] mt-1">
                      <Mail className="w-4 h-4" />
                      <span>{request.client_email}</span>
                    </div>
                    {request.message && (
                      <div className="flex items-start gap-2 text-sm text-[#687175] mt-2">
                        <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{request.message}</span>
                      </div>
                    )}
                    <p className="text-xs text-[#939BA0] mt-2">
                      {new Date(request.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(request.status)}
                  
                  {isAdmin && request.status !== 'completed' && (
                    <div className="flex gap-2 mt-2">
                      {request.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(request.id, 'contacted')}
                          className="px-3 py-1.5 text-sm font-medium text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors"
                          data-testid={`mark-contacted-${request.id}`}
                        >
                          Marquer contacté
                        </button>
                      )}
                      <button
                        onClick={() => handleStatusChange(request.id, 'completed')}
                        className="px-3 py-1.5 text-sm font-medium text-[#10D183] hover:bg-[#10D183]/10 rounded-lg transition-colors"
                        data-testid={`mark-completed-${request.id}`}
                      >
                        Marquer terminé
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CallbacksPage;
